/**
 * 角色管理 — GET/POST /api/admin/roles，DELETE /api/admin/roles/:id
 */
import { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface RoleRow {
  id: number;
  name: string;
  preset: string | null;
  description: string | null;
  menu_ids: number[];
  node_ids: number[];
}

interface MenuRow {
  id: number;
  name: string;
  route: string | null;
  category: string;
}

interface NodeRow {
  id: number;
  key: string;
  description: string;
  group: string | null;
}

export function RolesPage() {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('system.role.create');
  const canDelete = hasNode('system.role.delete');

  const { data: menus = [] } = useQuery({
    queryKey: ['admin', 'menus'],
    queryFn: () => api.get<MenuRow[]>('/api/admin/menus'),
  });

  const { data: nodes = [] } = useQuery({
    queryKey: ['admin', 'nodes'],
    queryFn: () => api.get<NodeRow[]>('/api/admin/nodes'),
  });

  async function handleCreate(values: {
    name: string;
    description?: string;
    menu_ids: number[];
    node_ids: number[];
  }) {
    try {
      await api.post('/api/admin/roles', {
        name: values.name,
        description: values.description,
        menu_ids: values.menu_ids ?? [],
        node_ids: values.node_ids ?? [],
      });
      message.success('角色已创建');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function handleDelete(row: RoleRow) {
    if (row.preset) {
      message.warning('预置角色不可删除');
      return;
    }
    Modal.confirm({
      title: `确定删除角色「${row.name}」？`,
      okType: 'danger',
      onOk: async () => {
        try {
          await api.del(`/api/admin/roles/${row.id}`);
          message.success('已删除');
          qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
        } catch (err) {
          message.error(err instanceof ApiError ? err.message : '删除失败');
        }
      },
    });
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '类型',
      dataIndex: 'preset',
      width: 120,
      render: (v: string | null) =>
        v ? <Tag color={v === 'super' ? 'red' : 'purple'}>{v}</Tag> : <Tag>自定义</Tag>,
    },
    { title: '说明', dataIndex: 'description', ellipsis: true },
    {
      title: '菜单数',
      key: 'mc',
      width: 80,
      render: (_: unknown, r: RoleRow) => r.menu_ids?.length ?? 0,
    },
    {
      title: '权限数',
      key: 'nc',
      width: 80,
      render: (_: unknown, r: RoleRow) => r.node_ids?.length ?? 0,
    },
    {
      title: '操作',
      key: 'act',
      width: 100,
      render: (_: unknown, row: RoleRow) =>
        canDelete && !row.preset ? (
          <Button type="link" danger size="small" onClick={() => handleDelete(row)}>
            删除
          </Button>
        ) : null,
    },
  ];

  const menuOptions = menus.map((m) => ({
    value: m.id,
    label: `${m.name}${m.route ? ` (${m.route})` : ''}`,
  }));
  const nodeOptions = nodes.map((n) => ({
    value: n.id,
    label: `${n.key} — ${n.description}`,
  }));

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setOpen(true)}>
            新建角色
          </Button>
        )}
      </Space>

      <DataTable<RoleRow>
        queryKey={['admin', 'roles']}
        endpoint="/api/admin/roles"
        columns={columns}
        rowKey="id"
      />

      <Modal
        open={open}
        title="新建角色"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="角色名称" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="2–32 字符" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="menu_ids" label="菜单">
            <Select mode="multiple" allowClear options={menuOptions} placeholder="可选" />
          </Form.Item>
          <Form.Item name="node_ids" label="权限节点">
            <Select mode="multiple" allowClear options={nodeOptions} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
